<template>
  <div class="leave-container">
    <!-- 페이지 헤더: 제목 + 연도 -->
    <div class="page-header">
      <h2>휴가 신청</h2>
      <el-select v-model="selectedYear" placeholder="연도" style="width: 110px">
        <el-option
          v-for="y in years"
          :key="y"
          :label="`${y}년`"
          :value="y"
        />
      </el-select>
    </div>

    <!-- 잔여 요약 칩 (통계 표 대체 — 상세는 아래 사용 내역에서) -->
    <div class="quota-chips" v-loading="loading">
      <div
        v-for="s in statistics"
        :key="s.leave_type"
        class="quota-chip"
        :class="{ 'quota-chip--over': s.exceeded_days > 0 }"
        :title="`사용 ${s.used_days}일${s.pending_days > 0 ? ` · 결재 대기 ${s.pending_days}일(잔여에 미반영)` : ''} · 기간 ${s.valid_from} ~ ${s.valid_to}`"
      >
        <span class="chip-type">{{ s.leave_type }}</span>
        <span class="chip-days">잔여 <b>{{ s.remaining_days }}</b> / {{ s.total_days }}일</span>
        <span v-if="s.pending_days > 0" class="chip-pending">대기 {{ s.pending_days }}일</span>
        <span v-if="s.exceeded_days > 0" class="chip-over">초과 {{ s.exceeded_days }}일</span>
      </div>
      <span v-if="error" class="chip-error">{{ error }}</span>
    </div>

    <!-- 연차 신청 (주 작업 — 최상단 배치) -->
    <el-card class="leave-request-card" shadow="never">
      <el-form :model="form" :rules="rules" ref="leaveForm" @submit.prevent="submitLeaveRequest" label-width="120px">
        <el-form-item label="휴가 구분"   prop="leave_type">
          

          <el-select v-model="form.leave_type" placeholder="휴가 구분 선택" style="width: 100%;" @change="onLeaveTypeChange">
            <el-option
              v-for="type in leaveTypes"
              :key="type.id"
              :label="type.name"
              :value="type.id"
            ></el-option>
          </el-select>
        </el-form-item>

        <!-- 반차 시간 선택 -->
        <el-form-item label="반차 시간" v-if="isHalfDayLeave" prop="session">
          <el-radio-group v-model="form.session" @change="calculateLeaveDays">
            <el-radio value="morning">오전</el-radio>
            <el-radio value="afternoon">오후</el-radio>
          </el-radio-group>
        </el-form-item>


        <el-form-item label="시작일" prop="start_date">
          <el-date-picker v-model="form.start_date" type="date"  @change="onStartDateChange"  placeholder="시작일 선택" style="width: 100%;"></el-date-picker>
        </el-form-item>

        <el-form-item label="종료일"  prop="end_date"  >
          <el-date-picker v-model="form.end_date" type="date" @change="calculateLeaveDays"  
            placeholder="종료일 선택" 
            style="width: 100%;" 
            :disabled="isHalfDayLeave"
            ></el-date-picker>
        </el-form-item>

             <!-- 계산된 휴가 일수 -->
       <el-form-item label="휴가 일수">
          <el-input v-model="calculatedDays" disabled />
          

          <!-- 연차/반차일대만 남은 연차일수 계산 -->
          <span v-if="form.leave_type === 1 || form.leave_type === 2"  style="color: red;" v-show="calculatedDays > remainingDays">
            남은 연차를 초과했습니다!
          </span>
        </el-form-item>


        <el-form-item label="사유"  prop="reason" >
          <el-input type="textarea"  v-model="form.reason" placeholder="1. 휴가사유 : 2. 현재 업무 인수인계내역 "></el-input>
        </el-form-item>


        <!-- 증빙 첨부 — LeaveType.requires_attachment 가 켜진 유형에서만 노출.
             유형명을 화면에 하드코딩하지 않는다(인사담당자가 휴가유형 화면에서 관리). -->
        <el-form-item v-if="requiresAttachment" label="증빙 서류" prop="attachments">
          <div class="att-block">
            <div class="att-row">
              <input
                ref="attachmentInput"
                type="file"
                multiple
                accept=".pdf,.png,.jpg,.jpeg,.gif,.bmp,.webp,.heic,.tif,.tiff"
                @change="onAttachmentPick"
              />
            </div>
            <div v-if="attachmentGuide" class="att-guide">{{ attachmentGuide }}</div>
            <div v-else class="att-guide">신청서 스캔본 또는 사진을 첨부하세요. (PDF·이미지, 각 10MB 이하)</div>
            <ul v-if="form.attachments.length" class="att-list">
              <li v-for="(f, i) in form.attachments" :key="i">
                <span class="att-name">{{ f.name }}</span>
                <span class="att-size">{{ formatFileSize(f.size) }}</span>
                <el-button text type="danger" size="small" @click="removeAttachment(i)">삭제</el-button>
              </li>
            </ul>
          </div>
        </el-form-item>

        <el-form-item label="승인자 설정" prop="approval_steps">
          <div class="approval-steps">
            <div v-for="(step, index) in form.approval_steps" :key="index" class="approval-step-row">
              <el-input
                v-model="step.sequence"
                type="number"
                class="step-seq"
                placeholder="순서"
              />
              <el-select
                v-model="step.approver"
                placeholder="승인자 선택"
                class="step-approver"
              >
                <el-option
                  v-for="user in employees"
                  :key="user.id"
                  :label="`${user.last_name} ${user.first_name}`"
                  :value="user.id"
                ></el-option>
              </el-select>
              <el-button text type="danger" @click="removeApprovalStep(index)">삭제</el-button>
            </div>
            <el-button plain size="small" @click="addApprovalStep">+ 승인 단계 추가</el-button>
          </div>
        </el-form-item>


        <el-form-item>
          <el-button type="primary" native-type="submit">신청하기</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 사용 내역 — 참고 정보라 기본 접힘 (펼치면 통계 상세 + 내역) -->
    <el-card class="leave-details-card" shadow="never">
      <el-collapse v-model="openPanels">
        <el-collapse-item name="history">
          <template #title>
            <span class="history-title">사용 내역 <b>{{ leaveDetails.length }}건</b> · 휴가사용 통계</span>
          </template>

          <h3 class="sub-title">휴가사용 통계</h3>
          <el-table :data="Array.isArray(statistics) ? statistics : []" size="small" style="width: 100%" border>
            <el-table-column prop="leave_type" label="휴가 구분" width="110" />
            <el-table-column prop="total_days" label="총 일수" width="90" align="right" />
            <el-table-column prop="remaining_days" label="남은 일수" width="90" align="right" />
            <el-table-column prop="used_days" label="사용 일수" width="90" align="right" />
            <el-table-column prop="pending_days" label="결재 대기" width="90" align="right">
              <template #default="{ row }">
                <span :class="{ 'pending-cell': row.pending_days > 0 }">{{ row.pending_days }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="exceeded_days" label="초과 사용" width="90" align="right" />
            <el-table-column prop="valid_from" label="사용 시작일" width="110" />
            <el-table-column prop="valid_to" label="사용 종료일" width="110" />
          </el-table>

          <h3 class="sub-title" style="margin-top: 14px;">사용 내역</h3>
          <el-table :data="Array.isArray(leaveDetails) ? leaveDetails : []" size="small" style="width: 100%" border>
            <el-table-column prop="start_date" label="시작일" width="110" />
            <el-table-column prop="end_date" label="종료일" width="110" />
            <el-table-column prop="leave_type__name" label="휴가 구분" width="110" />
            <el-table-column prop="days_requested" label="사용일수" width="90" align="right" />
            <el-table-column label="승인 상태" width="100">
              <template #default="scope">
                <span @click="showApprovalSteps(scope.row)" style="cursor: pointer; color: #409eff;">{{ mapApprovalStatus(scope.row.approval_status) }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="approved_by_name" label="승인자" width="100" />
            <el-table-column label="증빙" width="70" align="center">
              <template #default="scope">
                <el-tag v-if="scope.row.attachment_count > 0" type="success" effect="plain" size="small">
                  {{ scope.row.attachment_count }}건
                </el-tag>
                <span v-else style="color:#c0c4cc;">-</span>
              </template>
            </el-table-column>
            <el-table-column prop="rejection_reason" label="승인반려사유" min-width="120" />
          </el-table>
        </el-collapse-item>
      </el-collapse>
    </el-card>
  </div>

  <!-- 승인 단계별 상태조회 -->
  <el-dialog v-model="approvalDialogVisible" title="승인 단계 목록">
    <el-table :data="Array.isArray(approvalSteps) ? approvalSteps : []" style="width: 100%" border>
      <el-table-column prop="sequence" label="순서" width="80"></el-table-column>
      <el-table-column prop="approver_name" label="승인자" width="200"></el-table-column>
      <el-table-column prop="status" label="상태" width="150">
        <template #default="scope">
          {{ mapApprovalStatus(scope.row.status) }}
        </template>
      </el-table-column>
      <el-table-column prop="comments" label="코멘트"></el-table-column>
    </el-table>
    <template #footer>
      <el-button @click="approvalDialogVisible = false">닫기</el-button>
      <el-button 
        type="danger" 
        @click="cancelSelectedLeave"
        :disabled="Array.isArray(approvalSteps) && approvalSteps.some(step => step.status === 'approved')"
      >
        휴가신청취소
      </el-button>
    </template>
  </el-dialog>

</template>

<script setup>
import { ref ,watch, onMounted ,computed  } from "vue";
import axiosInstance from "@/api/axios"; // Axios 인스턴스 가져오기
import { dayjs ,ElMessage} from "element-plus"; // dayjs를 Element Plus에서 가져오기

const statistics = ref([]);
const leaveDetails = ref([]);
const leaveTypes = ref([]);

// 생일반차 LeaveType id 동적 조회 (시드 시점에 따라 id가 다를 수 있음)
const birthdayLeaveTypeId = computed(() => {
  const t = leaveTypes.value.find(t => t.name === '생일반차');
  return t ? t.id : null;
});

// 반차 여부 (id=2 또는 생일반차) — 반차 시간 선택, 0.5일 계산 모두 동일
const isHalfDayLeave = computed(() => {
  if (form.value.leave_type === 2) return true;
  if (birthdayLeaveTypeId.value !== null && form.value.leave_type === birthdayLeaveTypeId.value) return true;
  return false;
});
// 선택한 휴가 유형 객체 — 증빙 필수 여부/안내 문구는 서버 정책(LeaveType)에서 온다
const selectedLeaveType = computed(
  () => leaveTypes.value.find(t => t.id === form.value.leave_type) || null
);
const requiresAttachment = computed(() => !!selectedLeaveType.value?.requires_attachment);
const attachmentGuide = computed(() => selectedLeaveType.value?.attachment_guide || '');

// 증빙 첨부 파일 input 참조. 파일 목록 자체는 form.attachments 에 둔다.
// ⚠️ el-form 의 prop/required 검증은 **폼 모델(form)** 을 기준으로 돌기 때문에,
//    별도 ref 에 두면 검증기가 undefined 를 보고 항상 실패한다.
const attachmentInput = ref(null);

const holidays = ref([]);
const selectedLeaveId = ref(null);  // 선택된 휴가 ID 기억
const years = ref([]);
const selectedYear = ref(new Date().getFullYear());   // 🔹 기본=올해


// Approval status mapping
const mapApprovalStatus = (status) => {
  const statusMap = {
    pending: "대기",
    approved: "승인",
    rejected: "반려",
    canceled:"취소"
  };
  return statusMap[status] || "알 수 없음";
};

const form = ref({
  start_date: "",
  end_date: "",
  leave_type: "",
  days_requested:0,
  session:"full",//오전/오후/하루
  reason: "",
  attachments: [], // 증빙 첨부 (File 객체 배열) — el-form 검증 대상이라 폼 모델 안에 둔다
  approval_steps: [], // 승인 단계 정보
});

const calculatedDays = ref(0);
const remainingDays = ref(10); // 남은 연차
const employees = ref([]); // 직원 목록
const leaveForm = ref("leaveForm"); // form을 참조
const approvalDialogVisible = ref(false); // 다이얼로그 상태
const approvalSteps = ref([]); // 승인 단계 목록
const openPanels = ref([]); // 사용 내역 collapse — 기본 접힘


// 유효성 검사 규칙
const rules = computed(()=> ({
  leave_type: [{ required: true, message: "휴가 구분을 선택하세요.", trigger: "change" }],
  session: [
    {
      required: isHalfDayLeave.value,
      message: "반차일 경우 오전/오후를 선택하세요.",
      trigger: "change",
    },
  ],

  start_date: [
    { required: true, message: "시작일을 선택하세요.", trigger: "change" },
    {
      validator: (rule, value, callback) => {
        if (!value) {
          callback(new Error("시작일을 선택하세요."));
        } //else if (dayjs(value).isBefore(dayjs(), "day")) {
          //callback(new Error("시작일은 오늘 이후여야 합니다."));
        //} 
        else {
          callback();
        }        
      },
      trigger: "change",
    }
  ],
  end_date: [
    {
      validator: (rule, value, callback) => {
        if (!isHalfDayLeave.value && value < form.value.start_date) {
          callback(new Error("종료일은 시작일 이후여야 합니다."));
        } else {
          callback();
        }
      },
      trigger: "change",
    },
  ],
  reason: [{ required: true, message: "사유를 입력하세요.", trigger: "blur" }],
  attachments: [
    {
      required: requiresAttachment.value,
      type: "array",
      message: "증빙 서류를 첨부하세요.",
      trigger: "change",
    },
  ],
}));


/* ---------- 연도 선택 관련 ---------- */
// 연도 목록 조회(백엔드: /leaves/years/)
const fetchYears = async () => {
  try {
    const { data } = await axiosInstance.get("leaves/years/");
    // 방어 코드: 배열인지 확인
    years.value = Array.isArray(data) ? data : [];
    // 현재 값이 목록에 없으면 추가
    if (!years.value.includes(selectedYear.value)) {
      years.value.unshift(selectedYear.value);
    }
  } catch (e) {
    // 실패 시 최근 5개년 fallback
    const now = new Date().getFullYear();
    years.value = [now, now - 1, now - 2, now - 3, now - 4];
  }
};
// 연도 바뀌면 통계/내역/공휴일 동시 새로고침
watch(selectedYear, async () => {
  await Promise.all([fetchStatistics(), fetchHolidays()]);
});



// 승인 단계 추가
const addApprovalStep = () => {
  form.value.approval_steps.push({ sequence: form.value.approval_steps.length + 1, approver: "" });
};

// 승인 단계 삭제
const removeApprovalStep = (index) => {
  form.value.approval_steps.splice(index, 1);
};

// 직원 목록 가져오기
const fetchEmployees = async () => {
  try {
    const response = await axiosInstance.get("employees/?group=휴가승인자");
    employees.value = response.data;
  } catch (error) {
    console.error("Failed to fetch employees:", error);
  }
};

//승인단계별상태
const showApprovalSteps = async (leave) => {
  try {
    const response = await axiosInstance.get(`leaves/${leave.id}/approval-steps/`);
    approvalSteps.value = response.data.map((step) => ({
      sequence: step.sequence,
      approver_name: step.approver_name,
      status: step.status,
      comments: step.comments || "-",
    }));
    selectedLeaveId.value = leave.id;

    approvalDialogVisible.value = true;
  } catch (error) {
    console.error("승인 단계 조회 실패:", error);
    alert("승인 단계를 가져오는 중 오류가 발생했습니다.");
  }
};

//휴가신청취소처리 
const cancelSelectedLeave = async () => {
  if (!selectedLeaveId.value) return;

  try {
    await axiosInstance.post(`leaves/${selectedLeaveId.value}/cancel/`);
    ElMessage.success("휴가 신청이 취소되었습니다.");
    approvalDialogVisible.value = false;
    fetchStatistics();  // 새로고침
  } catch (error) {
    ElMessage.error("휴가 취소에 실패했습니다.");
    console.error("취소 실패:", error);
  }
};

// 휴가 유형 변경 시 처리
/* ---------- 증빙 첨부 ---------- */
const ATT_MAX_BYTES = 10 * 1024 * 1024;

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const onAttachmentPick = (e) => {
  const picked = Array.from(e.target.files || []);
  const tooBig = picked.filter(f => f.size > ATT_MAX_BYTES);
  if (tooBig.length) {
    ElMessage.error(`10MB를 초과한 파일은 제외했습니다: ${tooBig.map(f => f.name).join(", ")}`);
  }
  form.value.attachments.push(...picked.filter(f => f.size <= ATT_MAX_BYTES));
  // 같은 파일을 다시 고를 수 있도록 input 초기화
  if (attachmentInput.value) attachmentInput.value.value = "";
  leaveForm.value?.validateField?.("attachments");
};

const removeAttachment = (index) => {
  form.value.attachments.splice(index, 1);
  leaveForm.value?.validateField?.("attachments");
};

const clearAttachments = () => {
  form.value.attachments = [];
  if (attachmentInput.value) attachmentInput.value.value = "";
};

const onLeaveTypeChange = () => {
  // 유형이 바뀌면 이전 유형 기준으로 담아둔 증빙은 의미가 없다 → 초기화
  clearAttachments();

  if (isHalfDayLeave.value) {
    // 반차일 경우 종료일을 시작일과 동일하게 설정
    form.value.end_date = form.value.start_date;
    // 생일반차 등 신규 반차도 session 기본값 설정
    if (!form.value.session || form.value.session === "full") {
      form.value.session = "afternoon";
    }
  }
  if(!isHalfDayLeave.value){
    form.value.session ="full"; //하루로 초기화
  }
  calculateLeaveDays();
};

// 시작일 변경 시 처리
const onStartDateChange = () => {
  if (isHalfDayLeave.value) {
    // 반차일 경우 종료일을 시작일과 동일하게 설정
    form.value.end_date = form.value.start_date;
  }
  calculateLeaveDays();
};

// 휴가 일수 계산
const calculateLeaveDays = () => {
  // 반차 처리 (id=2 또는 생일반차)
  if (isHalfDayLeave.value) {
    calculatedDays.value = 0.5; // 반차는 0.5일 고정
    return;
  }

  // 시작일 또는 종료일이 없으면 반환
  if (!form.value.start_date || !form.value.end_date) return;

  const start = dayjs(form.value.start_date);
  const end = dayjs(form.value.end_date);

  let days = 0;
  let current = start.clone();

  while(current.isSame(end,'day')||current.isBefore(end,'day')){

    const isWeekend = current.day() === 0 || current.day() === 6;
    const isHoliday = holidays.value.some((holiday) => 
      dayjs(holiday).isSame(current, "day")
    );

    console.log("current=", current , "isWeekEnd=",isWeekend,"isHoliday=",isHoliday);

    if(!isWeekend && !isHoliday){
      days +=1;
    }

    current = current.add(1,'day');//새로운 객체로 갱신
  }

  calculatedDays.value = days;


  /*
  // 시작일부터 종료일까지 포함하여 반복
  for (let date = start; date.isSame(end, "day") || date.isBefore(end, "day"); date = date.add(1, "day")) {
    // 주말 또는 공휴일 제외
    const isWeekend = date.day() === 0 || date.day() === 6;
    const isHoliday = holidays.value.some((holiday) => dayjs(holiday.date).isSame(date, "day"));

    console.log("isWeekEnd = ",isWeekend ," isHoliday=",isHoliday)

    if (!isWeekend && !isHoliday) {
      days += 1;
    }
  }
  calculatedDays.value = days;
  */

  
};


const fetchHolidays = async () => {
  try {
    //const response = await axiosInstance.get("holidays/");

    console.log("Fetching holidays for year:", selectedYear.value);
    const response = await axiosInstance.get(`holidays/${selectedYear.value}/`);


    //holidays.value = response.data.holidays.map((date) => new Date(date)); // 날짜 객체로 변환
    holidays.value = response.data.holidays.map((h) => h.date); // 문자열 배열로 저장   
    console.log("Fetched holidays:", holidays.value);
  } catch (error) {
    console.error("Failed to fetch holidays:", error);
  }
};



/*
const fetchStatistics = async () => {
  const response = await axiosInstance.get("leave-request/statistics/");
  statistics.value = response.data.statistics;
  leaveDetails.value = response.data.details;
  leaveTypes.value = response.data.leave_types;
  remainingDays.value = response.data.statistics.remaining_days;

};
*/


const loading = ref(false)
const error = ref('')

const num = v => (v === null || v === undefined || isNaN(v)) ? 0 : Number(v)

const fetchStatistics = async () => {
  loading.value = true
  error.value = ''
  try {
    const { data } = await axiosInstance.get('leave-request/statistics/', {
      params: { year: selectedYear.value }
    })

    // 1) rows 추출: 배열로 바로 오거나, 객체 안에 statistics 배열로 올 수 있음
    const rows = Array.isArray(data)
      ? data
      : (Array.isArray(data?.statistics) ? data.statistics : [])

    // 2) 표 데이터 매핑(키가 달라도 방어적으로 처리)
    statistics.value = rows.map(x => ({
      leave_type: x.leave_type ?? x.type ?? '-',
      total_days: num(x.total_days ?? x.total),
      used_days: num(x.used_days ?? x.used),
      remaining_days: num(x.remaining_days ?? x.remaining),
      exceeded_days: num(x.exceeded_days ?? x.exceeded),
      pending_days: num(x.pending_days ?? x.pending),
      valid_from: x.valid_from ?? '',
      valid_to: x.valid_to ?? ''
    }))

    // 3) 상세/유형: 없으면 합리적인 기본값
    leaveDetails.value = Array.isArray(data?.details) ? data.details : []
    leaveTypes.value = Array.isArray(data?.leave_types)
      ? data.leave_types
      : [...new Set(statistics.value.map(r => r.leave_type).filter(Boolean))]

    // 4) 남은 일수: 객체형 응답에 summary가 있으면 사용, 없으면 합계로 계산
    if (typeof data?.statistics?.remaining_days === 'number') {
      remainingDays.value = data.statistics.remaining_days
    } else {
      remainingDays.value = statistics.value.reduce((acc, r) => acc + num(r.remaining_days), 0)
    }
  } catch (e) {
    error.value = e?.response?.data?.detail || e.message || '통계 조회 중 오류가 발생했습니다.'
  } finally {
    loading.value = false
  }
}

const submitLeaveRequest = async () => {
   // 유효성 검사 실행
   leaveForm.value.validate(async (valid) => {
    if (!valid) {
      // 유효성 검사 실패
      ElMessage.error("폼 데이터가 올바르지 않습니다. 필수 항목을 확인하세요.");
      return;
    }
   

      // 유효성 검사 성공 후 데이터 처리
      // 시작일과 종료일을 YYYY-MM-DD 형식으로 변환
      const pickedFiles = form.value.attachments || [];
      const formattedForm = {
        ...form.value,
        start_date: dayjs(form.value.start_date).format("YYYYMMDD"),
        end_date: dayjs(form.value.end_date).format("YYYYMMDD"),
        days_requested:calculatedDays.value,
        approval_steps: form.value.approval_steps, // 승인 단계 포함
      };
      // 파일 배열은 본문 필드가 아니라 multipart 파트로 따로 보낸다
      delete formattedForm.attachments;
      
      try{
        let response;
        if (pickedFiles.length > 0) {
          // 증빙이 있으면 multipart 로 보낸다. 서버가 신청+첨부를 한 트랜잭션에서
          // 저장하므로 "증빙 없는 결재 상신"이 생기지 않는다.
          // approval_steps 는 중첩 배열이라 FormData 에 그대로 못 담아 JSON 문자열로 보낸다.
          const fd = new FormData();
          Object.entries(formattedForm).forEach(([k, v]) => {
            if (k === "approval_steps" || v === null || v === undefined) return;
            fd.append(k, v);
          });
          fd.append("approval_steps", JSON.stringify(formattedForm.approval_steps || []));
          pickedFiles.forEach((f) => fd.append("attachments", f));
          // 기본 timeout(10s)은 파일 업로드에 짧다
          response = await axiosInstance.post("leaves/", fd, { timeout: 60000 });
        } else {
          response = await axiosInstance.post("leaves/", formattedForm);
        }
        console.log("[휴가 신청] 서버 응답 ✅", response.data);

        ElMessage.success("휴가 신청이 완료되었습니다.");

        // 폼 초기화 + 방금 신청한 내역이 보이도록 사용 내역 펼침
        form.value = {
          start_date: "",
          end_date: "",
          leave_type: "",
          days_requested: 0,
          session: "full",
          reason: "",
          attachments: [],
          approval_steps: [],
        };
        calculatedDays.value = 0;
        clearAttachments();
        openPanels.value = ["history"];

        fetchStatistics(); // 데이터 갱신

      }catch(error){
        if (Array.isArray(error.customErrors)) {
            error.customErrors.forEach((msg) => { 
              ElMessage.error(msg);
            }) ;
          }
      }     

  });
};

onMounted(() => {
  fetchYears()  
  fetchStatistics();
  fetchHolidays();
  fetchEmployees ();
});

// props가 변경되면 로컬 상태를 동기화
watch(
  () => form.value.leave_type,
  (newValue) => {
    //localSupplier.value = { ...newValue };
    console.log(newValue)
    
  }
);


</script>



<!--
  ⚠ 반드시 scoped 유지.
  과거 전역 h2 { font-size:1.5em; margin-bottom:20px } 가 새서
  이 화면 방문 후 전 화면의 h2 간격이 변하던 누수가 있었음 (2026-06-11 수정)
-->
<style scoped>
.leave-container {
  padding: 12px 20px;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}
.page-header h2 {
  margin: 0;
  font-size: 20px;
}

/* ===== 잔여 요약 칩 ===== */
.quota-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  min-height: 30px;
}
.quota-chip {
  display: inline-flex;
  align-items: baseline;
  gap: 8px;
  padding: 4px 12px;
  border: 1px solid #ebeef5;
  border-radius: 14px;
  background: #f8fafc;
  font-size: 13px;
  cursor: default;
}
.quota-chip .chip-type {
  font-weight: 600;
  color: #303133;
}
.quota-chip .chip-days {
  color: #606266;
  font-variant-numeric: tabular-nums;
}
.quota-chip .chip-days b {
  color: #409eff;
  font-size: 15px;
}
.quota-chip--over {
  border-color: #fde2e2;
  background: #fef0f0;
}
.quota-chip--over .chip-over {
  color: #f56c6c;
  font-weight: 600;
}
.quota-chip .chip-pending {
  color: #e6a23c;
  font-weight: 600;
}
.pending-cell {
  color: #e6a23c;
  font-weight: 600;
}
.att-block {
  width: 100%;
}
.att-guide {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
}
.att-list {
  margin: 6px 0 0;
  padding: 0;
  list-style: none;
}
.att-list li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 2px 0;
  font-size: 13px;
}
.att-list .att-name {
  color: #303133;
  word-break: break-all;
}
.att-list .att-size {
  color: #909399;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
}
.chip-error {
  color: #f56c6c;
  font-size: 12px;
}

.leave-request-card,
.leave-details-card {
  margin-bottom: 12px;
}

/* ===== 승인자 설정 ===== */
.approval-steps {
  width: 100%;
}
.approval-step-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}
.step-seq { width: 70px; flex-shrink: 0; }
.step-approver { width: 220px; }

/* ===== 사용 내역 (접힘) ===== */
.history-title {
  font-size: 14px;
  color: #303133;
}
.history-title b { color: #409eff; }
.sub-title {
  margin: 0 0 6px;
  font-size: 13px;
  color: #909399;
  font-weight: 600;
}
.leave-details-card :deep(.el-collapse) {
  border: none;
}
.leave-details-card :deep(.el-collapse-item__header) {
  border: none;
  height: 36px;
}
.leave-details-card :deep(.el-collapse-item__wrap) {
  border: none;
}

@media (max-width: 768px) {
  .leave-container {
    padding: 8px;
  }
}
</style>